/**
 * Timeline zoom + adaptive grid model.
 *
 * Zoom is exponential (every step multiplies) and spans from "whole arrangement
 * on screen" to "individual samples", so the useful range is enormous. Snap and
 * grid density are derived from the zoom level rather than fixed, which is what
 * makes precision editing feel right: coarse bars when zoomed out, ticks and
 * samples when zoomed in.
 */

export const MIN_PIXELS_PER_BEAT = 2;          // ~arrangement overview
export const MAX_PIXELS_PER_BEAT = 8_000_000;  // past sample level at 48kHz
export const DEFAULT_PIXELS_PER_BEAT = 100;
export const ZOOM_STEP = 1.25;

export const clampZoom = (pixelsPerBeat) => {
  const num = Number(pixelsPerBeat);
  if (!Number.isFinite(num)) return DEFAULT_PIXELS_PER_BEAT;
  return Math.max(MIN_PIXELS_PER_BEAT, Math.min(MAX_PIXELS_PER_BEAT, num));
};

/** Multiply zoom by `factor` (exponential feel), clamped to the usable range. */
export const scaleZoom = (pixelsPerBeat, factor) => clampZoom(pixelsPerBeat * factor);

/**
 * Zoom while keeping the beat under the cursor pinned in place — the behaviour
 * that makes deep zooming usable. Returns the new zoom and the scroll offset
 * that preserves `anchorBeat` at `anchorClientX` pixels from the viewport edge.
 */
export function zoomAtAnchor({ pixelsPerBeat, factor, anchorBeat, anchorOffsetPx }) {
  const nextZoom = scaleZoom(pixelsPerBeat, factor);
  const nextScrollLeft = Math.max(0, (anchorBeat * nextZoom) - anchorOffsetPx);
  return { pixelsPerBeat: nextZoom, scrollLeft: nextScrollLeft };
}

/** Beat under a viewport x position, given the current scroll offset. */
export function beatAtPixel(xInViewport, scrollLeft, pixelsPerBeat) {
  return Math.max(0, (scrollLeft + xInViewport) / Math.max(0.0001, pixelsPerBeat));
}

// Musical grid divisions, coarse → fine, in beats (4/4).
const GRID_DIVISIONS = [
  16, 8, 4, 2, 1,
  1 / 2, 1 / 4, 1 / 8, 1 / 16, 1 / 32, 1 / 64, 1 / 128, 1 / 256, 1 / 512
];

/**
 * Finest musical division whose lines stay at least `minPixelGap` apart, so the
 * grid refines as you zoom without ever becoming a solid smear.
 */
export function gridDivisionForZoom(pixelsPerBeat, minPixelGap = 12) {
  let chosen = GRID_DIVISIONS[0];
  for (let i = 0; i < GRID_DIVISIONS.length; i += 1) {
    if (GRID_DIVISIONS[i] * pixelsPerBeat >= minPixelGap) chosen = GRID_DIVISIONS[i];
    else break;
  }
  return chosen;
}

/**
 * Snap resolution in beats for the current zoom. `auto` follows the visible
 * grid; an explicit value is honoured as-is. Below sample-adjacent zoom levels
 * this returns 0, meaning "no snapping — free positioning".
 */
export function snapResolution(pixelsPerBeat, snapValue = 'auto', minPixelGap = 12) {
  if (snapValue === 'off' || snapValue === 0) return 0;
  if (snapValue === 'auto' || snapValue === undefined || snapValue === null) {
    return gridDivisionForZoom(pixelsPerBeat, minPixelGap);
  }
  const num = Number(snapValue);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

/** Snap a beat to `resolution`; resolution 0 passes the value through. */
export function snapBeat(beat, resolution) {
  if (!resolution || resolution <= 0) return Math.max(0, beat);
  return Math.max(0, Math.round(beat / resolution) * resolution);
}

/** Human label for the current grid, e.g. "1/16" or "2 bars". */
export function formatDivision(division) {
  if (division >= 4) return `${division / 4} bar${division / 4 === 1 ? '' : 's'}`;
  if (division >= 1) return `${division} beat${division === 1 ? '' : 's'}`;
  return `1/${Math.round(1 / division)}`;
}
